import { z } from "zod";
// =============================================================================
// ENUMS
// =============================================================================
const toUpperCase = (val) => typeof val === "string" ? val.toUpperCase() : val;
export const ConversationTypeSchema = z.preprocess(toUpperCase, z.enum(["PRE_BOOKING", "POST_BOOKING"]));
export const ConversationStatusSchema = z.preprocess(toUpperCase, z.enum(["ACTIVE", "CLOSED", "INVALIDATED"]));
export const ClosedReasonSchema = z.preprocess(toUpperCase, z.enum(["USER_CLOSED", "INACTIVITY_TIMEOUT", "REFERENCE_INVALIDATED"]));
export const MessageTypeSchema = z.preprocess(toUpperCase, z.enum(["USER", "SYSTEM"]));
// =============================================================================
// ATTACHMENT
// =============================================================================
export const AttachmentSchema = z.object({
    id: z.string(),
    url: z.string().url(),
    type: z.enum(["image", "pdf"]),
    name: z.string(),
});
// =============================================================================
// PARTICIPANT
// =============================================================================
const OptionalOrgIdSchema = z.coerce.string().nullable().optional();
export const PeerIdentitySchema = z
    .object({
    id: z.coerce.string().optional(),
    name: z.string().optional(),
    display_name: z.string().optional(),
    org_id: OptionalOrgIdSchema,
    org_name: z.string().nullable().optional(),
    org_type: z.string().nullable().optional(),
    role: z.string().optional(),
})
    .passthrough();
export const ParticipantSchema = z
    .object({
    id: z.coerce.string(),
    name: z.string(),
    role: z.string().optional(),
    org_id: OptionalOrgIdSchema,
    org_name: z.string().nullable().optional(),
    org_type: z.string().nullable().optional(),
})
    .passthrough();
// =============================================================================
// CONVERSATION
// =============================================================================
export const ConversationSchema = z.object({
    id: z.coerce.string(),
    conversation_type: ConversationTypeSchema,
    status: ConversationStatusSchema,
    closure_reason: ClosedReasonSchema.nullable().optional(),
    service_id: z.coerce.string().optional(),
    service_name_snapshot: z.string().nullable().optional(),
    itinerary_id: z.coerce.string().nullable().optional(),
    itinerary_code_snapshot: z.string().nullable().optional(),
    booking_id: z.coerce.string().nullable().optional(),
    slot_id: z.coerce.string().nullable().optional(),
    provider_id: z.coerce.string().optional(),
    concierge_id: z.coerce.string().optional(),
    concierge_org_id: OptionalOrgIdSchema,
    provider_org_id: OptionalOrgIdSchema,
    peer_identity: PeerIdentitySchema.nullable().optional(),
    peer: PeerIdentitySchema.nullable().optional(),
    last_message_at: z.string().nullable().optional(),
    last_message_preview: z.string().nullable().optional(),
    unread_message_count: z.coerce.number().int().nonnegative().default(0),
    has_unread_messages: z.boolean().default(false),
    created_at: z.string(),
    participants: z.array(ParticipantSchema).default([]),
});
// =============================================================================
// MESSAGE
// =============================================================================
export const MessageSchema = z.object({
    id: z.coerce.string(),
    conversation_id: z.coerce.string(),
    message_type: MessageTypeSchema,
    message: z.string().nullable(),
    sender_id: z.coerce.string().nullable().optional(),
    sender_org_id: OptionalOrgIdSchema,
    sender_org_name: z.string().nullable().optional(),
    sender_org_type: z.string().nullable().optional(),
    concierge_org_id: OptionalOrgIdSchema,
    provider_org_id: OptionalOrgIdSchema,
    sender: PeerIdentitySchema.nullable().optional(),
    peer_identity: PeerIdentitySchema.nullable().optional(),
    attachment_url: z.string().nullable().optional(),
    attachment_name: z.string().nullable().optional(),
    attachment_mime_type: z.string().nullable().optional(),
    attachment_size: z.coerce.number().nullable().optional(),
    edited_at: z.string().nullable().optional(),
    deleted_at: z.string().nullable().optional(),
    created_at: z.string(),
});
// =============================================================================
// FILTERS
// =============================================================================
export const ConversationFiltersSchema = z.object({
    itineraryId: z.string().optional(),
    serviceId: z.string().optional(),
    dateFrom: z.string().optional(),
    dateTo: z.string().optional(),
});
